import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import {
  cancelSkillAgentTask,
  dispatchSkillAgentTask,
  getSkillAgentTaskById,
  markSkillAgentTaskDone,
  updateSkillAgentTask,
} from "../../api/Skills/skillAgentTasksApi";
import { listSkillFichas } from "../../api/Skills/skillFichasApi";
import { taskStatusColor } from "./skillFichaHelpers";
import {
  taskStatusLabel,
  taskTypeLabel,
} from "./skillAgentTaskTree";

const TASK_TYPE_KEYS = [
  "presentacion",
  "propuestas",
  "siluetas",
  "mood",
  "revision",
  "otro",
];

export default function SkillAgentTaskDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [task, setTask] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [form, setForm] = useState({
    Titulo: "",
    Brief: "",
    Temporada: "",
    TipoTarea: "otro",
    SkillFichaId: "",
    ConstraintsJson: { tipología: "", coste: "", mercado: "", refsAncla: "" },
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const decoded = jwtDecode(token);
      const user = await getUserById(decoded.userId);
      if (!user?.Permiso_TareasAgentes) {
        setForbidden(true);
        return;
      }
      const [row, fichasData] = await Promise.all([
        getSkillAgentTaskById(id),
        listSkillFichas({}),
      ]);
      setTask(row);
      setFichas(Array.isArray(fichasData) ? fichasData : []);
      const c =
        row.ConstraintsJson && typeof row.ConstraintsJson === "object"
          ? row.ConstraintsJson
          : {};
      setForm({
        Titulo: row.Titulo || "",
        Brief: row.Brief || "",
        Temporada: row.Temporada || "",
        TipoTarea: row.TipoTarea || "otro",
        SkillFichaId: row.SkillFichaId ? String(row.SkillFichaId) : "",
        ConstraintsJson: {
          tipología: c.tipología || c.tipologia || "",
          coste: c.coste || "",
          mercado: c.mercado || "",
          refsAncla: c.refsAncla || "",
        },
      });
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) setForbidden(true);
      else setError(t("skillAgent.taskDetail.loadError"));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const updated = await updateSkillAgentTask(id, {
        Titulo: form.Titulo,
        Brief: form.Brief,
        Temporada: form.Temporada,
        TipoTarea: form.TipoTarea,
        SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
        ConstraintsJson: form.ConstraintsJson,
      });
      setTask(updated);
      setOkMsg(t("skillAgent.taskDetail.saved"));
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.create.taskError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDispatch = async (promptOnly = true) => {
    setSaving(true);
    setError("");
    try {
      const res = await dispatchSkillAgentTask(id, { promptOnly });
      setTask(res.task);
      setOkMsg(
        res.hasPublishedSkill
          ? t("skillAgent.taskDetail.preparedWithSkill", {
              version: res.task.SkillVersionUsed || "?",
            })
          : t("skillAgent.taskDetail.preparedNoPublishedSkill"),
      );
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.dispatchError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDone = async () => {
    setSaving(true);
    try {
      const updated = await markSkillAgentTaskDone(id);
      setTask(updated);
      setOkMsg(t("skillAgent.taskDetail.markedDoneTask"));
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.taskDetail.errorGeneric"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t("skillAgent.taskDetail.confirmCancel"))) return;
    setSaving(true);
    try {
      const updated = await cancelSkillAgentTask(id);
      setTask(updated);
    } finally {
      setSaving(false);
    }
  };

  const copyPrompt = async () => {
    if (!task?.PromptSnapshot) return;
    try {
      await navigator.clipboard.writeText(task.PromptSnapshot);
      setOkMsg(t("skillAgent.taskDetail.promptCopied"));
    } catch {
      setError(t("skillAgent.taskDetail.copyFailed"));
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">
          {t("skillAgent.taskDetail.forbidden")}
        </Alert>
      </Box>
    );
  }

  if (loading || !task) {
    return (
      <Box
        sx={{
          ...contentMargin,
          display: "flex",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const readOnly = ["running", "done"].includes(task.Status);

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        maxWidth: 960,
        px: { xs: 1.5, md: 3 },
        py: 2,
        ...contentMargin,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/skills/tareas")}
          size="small"
        >
          {t("skillAgent.taskDetail.backToTasks")}
        </Button>
        <Typography variant="h5" fontWeight={800} sx={{ flex: 1 }}>
          {t("skillAgent.taskDetail.orderLabel", { id: task.id })} ·{" "}
          {task.Titulo}
        </Typography>
        <Chip
          label={taskStatusLabel(task.Status)}
          color={taskStatusColor(task.Status)}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            label={t("skillAgent.form.title")}
            size="small"
            value={form.Titulo}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Titulo: e.target.value }))}
            sx={pageFieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl
              size="small"
              sx={{ flex: 1, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>{t("skillAgent.form.clientFicha")}</InputLabel>
              <Select
                label={t("skillAgent.form.clientFicha")}
                value={form.SkillFichaId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">—</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>{t("skillAgent.form.taskType")}</InputLabel>
              <Select
                label={t("skillAgent.form.taskType")}
                value={form.TipoTarea}
                onChange={(e) =>
                  setForm((p) => ({ ...p, TipoTarea: e.target.value }))
                }
              >
                {TASK_TYPE_KEYS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {taskTypeLabel(k)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t("skillAgent.form.season")}
              size="small"
              value={form.Temporada}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({ ...p, Temporada: e.target.value }))
              }
              sx={{ minWidth: 120, ...pageFieldSx }}
            />
          </Stack>
          <TextField
            label={t("skillAgent.form.brief")}
            multiline
            minRows={4}
            value={form.Brief}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Brief: e.target.value }))}
            sx={pageFieldSx}
          />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            {t("skillAgent.form.constraintsHeading")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            {["tipología", "coste", "mercado", "refsAncla"].map((key) => (
              <TextField
                key={key}
                label={key}
                size="small"
                value={form.ConstraintsJson[key] || ""}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      [key]: e.target.value,
                    },
                  }))
                }
                sx={{ flex: 1, ...pageFieldSx }}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!readOnly && (
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                {t("skillAgent.common.save")}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              sx={pagePrimaryButtonSx}
              disabled={saving || task.Status === "done"}
              onClick={() => handleDispatch(true)}
            >
              {t("skillAgent.taskDetail.prepare")}
            </Button>
            {task.Status !== "done" && task.Status !== "cancelled" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={saving}
                onClick={handleDone}
              >
                {t("skillAgent.taskDetail.markDone")}
              </Button>
            )}
            {task.Status !== "cancelled" && task.Status !== "done" && (
              <Button color="inherit" disabled={saving} onClick={handleCancel}>
                {t("skillAgent.common.cancel")}
              </Button>
            )}
            {task.DriveFolderUrl && (
              <Button
                startIcon={<OpenInNewOutlinedIcon />}
                href={task.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("skillAgent.taskDetail.openDrive")}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.default, 0.6),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {t("skillAgent.taskDetail.promptHeading")}
            {task.DispatchMode ? ` · ${task.DispatchMode}` : ""}
            {task.SkillVersionUsed != null
              ? ` · skill v${task.SkillVersionUsed}`
              : ""}
          </Typography>
          {task.PromptSnapshot && (
            <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={copyPrompt}
            >
              {t("skillAgent.common.copy")}
            </Button>
          )}
        </Stack>
        {task.ResultSummary && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {task.ResultSummary}
          </Alert>
        )}
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha("#000", 0.04),
            fontSize: "0.78rem",
            whiteSpace: "pre-wrap",
            maxHeight: 420,
            overflow: "auto",
          }}
        >
          {task.PromptSnapshot || t("skillAgent.taskDetail.promptEmpty")}
        </Box>
      </Paper>
    </Box>
  );
}
